import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Search, X, Edit2, Save, Trash2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { SiteImageUpload } from './SiteImageUpload';
import {
  getStockImages,
  createStockImage,
  updateStockImage,
  deleteStockImage,
  type StockImage,
} from '@/lib/admin/site-images';

const STOCK_CATEGORIES = [
  { value: '1bed', label: '1 Bedroom' },
  { value: '2bed', label: '2 Bedroom' },
  { value: '3bed', label: '3 Bedroom' },
  { value: 'amenity', label: 'Amenity' },
  { value: 'exterior', label: 'Exterior' },
  { value: 'other', label: 'Other' },
];

export function StockImageManager() {
  const [images, setImages] = useState<StockImage[]>([]);
  const [filteredImages, setFilteredImages] = useState<StockImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    loadStockImages();
  }, []);

  useEffect(() => {
    filterImages();
  }, [images, searchQuery, categoryFilter]);

  const loadStockImages = async () => {
    try {
      setLoading(true);
      const data = await getStockImages();
      setImages(data);
    } catch (error) {
      console.error('Error loading stock images:', error);
      toast({
        title: 'Error',
        description: 'Failed to load stock images',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const filterImages = () => {
    let filtered = [...images];

    // Filter by category
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(img => img.category === categoryFilter);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(img =>
        img.name.toLowerCase().includes(query) ||
        img.category.toLowerCase().includes(query)
      );
    }

    setFilteredImages(filtered);
  };

  const handleImagesUploaded = async (urls: string[]) => {
    try {
      setSaving(true);
      
      // For stock images, we need to create entries in the database
      // The URLs are already uploaded, now we create records
      // In a real implementation, you might want to prompt for name/category per image
      // For now, we'll create with default values
      const newImages: StockImage[] = [];
      
      for (const url of urls) {
        const imageName = url.split('/').pop()?.replace(/-\d+\.(avif|webp|jpg)$/, '') || 'stock-image';
        const category = imageName.split('-')[0] || 'other';
        
        const created = await createStockImage({
          name: imageName,
          category: category,
          url: url,
        });
        
        if (created) {
          newImages.push(created);
        }
      }

      if (newImages.length > 0) {
        setImages([...images, ...newImages]);
        toast({
          title: 'Success',
          description: `Created ${newImages.length} stock image record(s)`,
        });
      }
    } catch (error) {
      console.error('Error handling image upload:', error);
      toast({
        title: 'Error',
        description: 'Failed to create stock image records',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleStartEdit = (image: StockImage) => {
    setEditingId(image.id);
    setEditName(image.name);
    setEditCategory(image.category);
  };

  const handleSaveEdit = async (id: string) => {
    try {
      setSaving(true);
      const updated = await updateStockImage(id, {
        name: editName,
        category: editCategory,
      });

      if (updated) {
        setImages(images.map(img => img.id === id ? updated : img));
        setEditingId(null);
        toast({
          title: 'Success',
          description: 'Stock image updated',
        });
      } else {
        throw new Error('Failed to update');
      }
    } catch (error) {
      console.error('Error updating stock image:', error);
      toast({
        title: 'Error',
        description: 'Failed to update stock image',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditCategory('');
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this stock image?')) {
      return;
    }

    try {
      setSaving(true);
      const success = await deleteStockImage(id);
      if (success) {
        setImages(images.filter(img => img.id !== id));
        toast({
          title: 'Success',
          description: 'Stock image deleted',
        });
      } else {
        throw new Error('Failed to delete');
      }
    } catch (error) {
      console.error('Error deleting stock image:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete stock image',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Stock Images</CardTitle>
        <CardDescription>
          Manage reusable stock images for property types and amenities. These images can be used across multiple units.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Upload Section */}
        <SiteImageUpload
          imageType="stock"
          existingImageUrls={[]}
          onImageUpdated={() => {}}
          onImagesUpdated={handleImagesUploaded}
          label="Upload Stock Images"
          description="Upload multiple stock images. They will be optimized and added to the library."
          maxImages={50}
        />

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Label htmlFor="search">Search</Label>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="search"
                placeholder="Search by name or category..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
          <div className="sm:w-48">
            <Label htmlFor="category">Category</Label>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger id="category">
                <SelectValue placeholder="All categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {STOCK_CATEGORIES.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Images Grid */}
        {filteredImages.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredImages.map((image) => (
              <div key={image.id} className="relative group border rounded-lg overflow-hidden">
                <img
                  src={image.url}
                  alt={image.name}
                  className="w-full h-32 object-cover"
                />
                <div className="p-2 space-y-1">
                  {editingId === image.id ? (
                    <div className="space-y-2">
                      <Input
                        className="h-8 text-sm"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        placeholder="Image name"
                      />
                      <Select value={editCategory} onValueChange={setEditCategory}>
                        <SelectTrigger className="h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STOCK_CATEGORIES.map(cat => (
                            <SelectItem key={cat.value} value={cat.value}>
                              {cat.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => handleSaveEdit(image.id)}
                          disabled={saving}
                        >
                          <Save className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleCancelEdit}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="text-xs font-medium truncate">{image.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {STOCK_CATEGORIES.find(c => c.value === image.category)?.label || image.category}
                      </p>
                      <div className="flex gap-1 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleStartEdit(image)}
                          disabled={saving}
                        >
                          <Edit2 className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDelete(image.id)}
                          disabled={saving}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            {searchQuery || categoryFilter !== 'all' 
              ? 'No images match your filters'
              : 'No stock images uploaded yet'}
          </div>
        )}

        {/* Stats */}
        <div className="text-sm text-muted-foreground">
          Showing {filteredImages.length} of {images.length} stock images
        </div>
      </CardContent>
    </Card>
  );
}

